import React from 'react';
import { Box, Chip, CircularProgress } from '@mui/material';
import { MenuBook as SourceIcon } from '@mui/icons-material';
import SourceBadge from './SourceBadge';
import SourcesModal from './SourcesModal';
import type { Source } from '../types/api';
import { useSources } from '../contexts/SourcesContext';

const COLLAPSE_THRESHOLD = 3;

export interface InlineSourceBadgesProps {
  sourceIds: number[];
}

const InlineSourceBadges: React.FC<InlineSourceBadgesProps> = ({ sourceIds }) => {
  const { ensureSources } = useSources();
  const [sources, setSources] = React.useState<Source[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedSourceIds, setSelectedSourceIds] = React.useState<number[]>([]);

  React.useEffect(() => {
    const fetchSources = async () => {
      if (sourceIds.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const sourcesData = await ensureSources(sourceIds);
        setSources(sourcesData);
      } catch (err) {
        console.error('Error fetching sources for badges:', err);
        // On error, create placeholder sources with just IDs
        setSources(sourceIds.map(id => ({ id, name: `Source ${id}` })));
      } finally {
        setLoading(false);
      }
    };

    fetchSources();
  }, [sourceIds, ensureSources]);

  const handleBadgeClick = (sourceId: number) => {
    setSelectedSourceIds([sourceId]);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSourceIds([]);
  };

  const handleMultipleSourcesClick = () => {
    setSelectedSourceIds(sourceIds);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', mx: 0.5 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (sources.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
          verticalAlign: 'middle',
          my: 0.25,
        }}
      >
        {sources.length >= COLLAPSE_THRESHOLD ? (
          <Chip
            icon={<SourceIcon sx={{ fontSize: '0.875rem' }} />}
            label={`Multiple sources (${sources.length})`}
            size="small"
            variant="outlined"
            onClick={handleMultipleSourcesClick}
            sx={{
              cursor: 'pointer',
              fontSize: '0.75rem',
              height: 24,
              '& .MuiChip-label': {
                px: 0.5,
              },
              '& .MuiChip-icon': {
                ml: 0.5,
              },
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          />
        ) : (
          sources.map((source) => (
            <SourceBadge
              key={source.id}
              source={source}
              onClick={handleBadgeClick}
            />
          ))
        )}
      </Box>
      <SourcesModal
        open={modalOpen}
        onClose={handleCloseModal}
        sourceIds={selectedSourceIds}
      />
    </>
  );
};

export default InlineSourceBadges;
